import {
  AssignmentModel,
  AssignmentStatusEnum,
  AssignmentTypeEnum,
  AssignmentViewModel,
  CaseTypeEnum,
} from 'api/model';
import {
  assignmentFormToAssignment,
  convertStringToExpectedExpensesArray,
  fieldTesterShouldBookTheirOwnTicketByDefault,
  getExpectedExpensesBasedOnSelfTransportation,
  getUnExceptedExpensesBasedOnSelfTransportation,
  hourInMilliseconds,
  travelsBetweenDifferentCounties,
} from '../DetailedCaseFormProvider/useCaseAssignmentForms';
import { useEffect, useRef } from 'react';
import { BasicFormState } from 'hooks/useForm';
import { toInputDateTimeString } from 'utils/date-helpers';
import useUsers from 'contexts/basicData/useUsers';
import useTranslations from 'contexts/basicData/useTranslations';
import { hardCodedEstimatedDurations } from 'constants/AppConstants';
import { useAverageAssignmentDurationsGetAverageAssignmentDurationHook } from 'api/average-assignment-durations/average-assignment-durations';
import esitimatedAssignmentDurations from 'utils/esitimatedAssignmentDurations';
import useZipCodeAreas from 'hooks/useZipCodeAreas';
import {
  AssignmentField,
  isSelfTransportationAssignment,
} from '../DetailedCaseFormProvider/types';
import { isStaffRentalCaseType } from './utils';

type Props = {
  currentAssignment: AssignmentModel;
  currentCaseType: CaseTypeEnum;
  multiplePurchasesList: AssignmentViewModel[];
  state: BasicFormState<AssignmentField>;
  unmodifiedAssignment?: AssignmentModel;

  thereAreMultiplePurchasesForAssignedUser: (userId: number) => boolean;
  onChange: (
    assignmentId: number,
    field: AssignmentField,
    value: string
  ) => void;
};
const useCaseAssignmentFormsEffects = ({
  currentAssignment,
  currentCaseType,
  multiplePurchasesList,
  state,
  unmodifiedAssignment,

  onChange,
  thereAreMultiplePurchasesForAssignedUser,
}: Props) => {
  const assignmentId = currentAssignment.assignmentID;
  const usersById = useUsers();
  const { zipCodeAreas } = useTranslations();

  const avgDurationCallTimeoutRef = useRef<NodeJS.Timeout>();
  const zipCodeAreaHook = useZipCodeAreas();

  const getAverageAssignmentDurationCall =
    useAverageAssignmentDurationsGetAverageAssignmentDurationHook();

  const mainAssignmentFromCounty = state[AssignmentField.fromCounty];
  const mainAssignmentToCounty = state[AssignmentField.toCounty];

  // when the main assignment is a personal transprot
  useEffect(() => {
    if (currentAssignment.assignmentStatusID === AssignmentStatusEnum.Approved)
      return;
    if (
      isSelfTransportationAssignment(currentAssignment.assignmentTypeID)
    ) {
      // make sure that there is an expected expense based on the type of transportation
      const travelsBetweenCounties = travelsBetweenDifferentCounties(
        mainAssignmentFromCounty,
        mainAssignmentToCounty
      );
      const expectedExpense = getExpectedExpensesBasedOnSelfTransportation(
        currentAssignment.assignmentTypeID,
        travelsBetweenCounties
      );
      const notExpectedExpenses =
        getUnExceptedExpensesBasedOnSelfTransportation(
          currentAssignment.assignmentTypeID,
          travelsBetweenCounties
        );

      if (expectedExpense === undefined) {
        return;
      }
      const existingExpectedExpense = currentAssignment.expectedExpenses.find(
        (e) => e.type === expectedExpense
      );

      if (!existingExpectedExpense) {
        const updatedExpectedExpenses = [
          ...(currentAssignment.expectedExpenses.map((e) => e.type) ?? []),
          expectedExpense,
        ];
        // remove any not expected expenses
        const updatedExpectedExpensesList = updatedExpectedExpenses.filter(
          (e) => !notExpectedExpenses.includes(e)
        );

        onChange(
          assignmentId,
          AssignmentField.expectedExpenses,
          Array.from(updatedExpectedExpensesList).join(',')
        );
      }
    }
    // expectedExpenses ska INTE vara med i dependencies: effekten sätter bara en
    // default när uppdragstyp/län ändras. Med expectedExpenses som dependency
    // återställs utgiften direkt när användaren bockar ur den manuellt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCaseType,
    currentAssignment?.assigneeShouldBookTheirOwnTicket,
    currentAssignment.assignmentTypeID,
    assignmentId,
    mainAssignmentFromCounty,
    mainAssignmentToCounty,
  ]);

  // when we are adding inline personal transport to the assignment, we need to make sure that the expected expenses are set
  const personalTransportationToAssignmentAssignmentTypeID =
    state[AssignmentField.personalTransportationToAssignmentAssignmentTypeID];
  const personalTransportationToAssignmentCounty =
    state[AssignmentField.personalTransportationToAssignmentCounty];
  useEffect(() => {
    const newValue = Number(
      state[AssignmentField.personalTransportationToAssignmentAssignmentTypeID]
    ) as unknown as AssignmentTypeEnum;

    if (isSelfTransportationAssignment(newValue)) {
      // jämför den personliga transportens start-län med huvuduppdragets start-län
      const travelsBetweenCounties = travelsBetweenDifferentCounties(
        personalTransportationToAssignmentCounty,
        mainAssignmentFromCounty
      );
      const expectedExpense = getExpectedExpensesBasedOnSelfTransportation(
        newValue,
        travelsBetweenCounties
      );
      const notExpectedExpenses =
        getUnExceptedExpensesBasedOnSelfTransportation(
          newValue,
          travelsBetweenCounties
        );
      if (expectedExpense === undefined) {
        return;
      }
      const existingExpectedExpenses = convertStringToExpectedExpensesArray(
        state[AssignmentField.personalTransportationToExpectedExpenses]
      );
      const existingExpectedExpense = existingExpectedExpenses.find(
        (e) => e.type === expectedExpense
      );

      if (!existingExpectedExpense) {
        const updatedExpectedExpenses = [
          ...(existingExpectedExpenses.map((e) => e.type) ?? []),
          expectedExpense,
        ];
        // remove any not expected expenses
        const updatedExpectedExpensesList = updatedExpectedExpenses.filter(
          (e) => !notExpectedExpenses.includes(e)
        );

        onChange(
          assignmentId,
          AssignmentField.personalTransportationToExpectedExpenses,
          Array.from(updatedExpectedExpensesList).join(',')
        );

        onChange(
          assignmentId,
          AssignmentField.personalTransportationToAssigneeShouldBookTheirOwnTicket,
          fieldTesterShouldBookTheirOwnTicketByDefault(newValue)
            ? 'true'
            : 'false'
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    personalTransportationToAssignmentAssignmentTypeID,
    personalTransportationToAssignmentCounty,
    mainAssignmentFromCounty,
  ]);

  // when we are adding inline personal transport from the assignment, we need to make sure that the expected expenses are set
  const personalTransportationFromAssignmentAssignmentTypeID =
    state[AssignmentField.personalTransportationFromAssignmentAssignmentTypeID];
  const personalTransportationFromAssignmentCounty =
    state[AssignmentField.personalTransportationFromAssignmentCounty];
  useEffect(() => {
    const newValue = Number(
      state[
        AssignmentField.personalTransportationFromAssignmentAssignmentTypeID
      ]
    ) as unknown as AssignmentTypeEnum;
    if (isSelfTransportationAssignment(newValue)) {
      // jämför den personliga transportens slut-län med huvuduppdragets slut-län
      const travelsBetweenCounties = travelsBetweenDifferentCounties(
        personalTransportationFromAssignmentCounty,
        mainAssignmentToCounty
      );
      const expectedExpense = getExpectedExpensesBasedOnSelfTransportation(
        newValue,
        travelsBetweenCounties
      );
      const notExpectedExpenses =
        getUnExceptedExpensesBasedOnSelfTransportation(
          newValue,
          travelsBetweenCounties
        );
      if (expectedExpense === undefined) {
        return;
      }
      const existingExpectedExpenses = convertStringToExpectedExpensesArray(
        state[AssignmentField.personalTransportationFromExpectedExpenses]
      );
      const existingExpectedExpense = existingExpectedExpenses.find(
        (e) => e.type === expectedExpense
      );
      if (!existingExpectedExpense) {
        const updatedExpectedExpenses = [
          ...(existingExpectedExpenses.map((e) => e.type) ?? []),
          expectedExpense,
        ];
        // remove any not expected expenses
        const updatedExpectedExpensesList = updatedExpectedExpenses.filter(
          (e) => !notExpectedExpenses.includes(e)
        );
        onChange(
          assignmentId,
          AssignmentField.personalTransportationFromExpectedExpenses,
          Array.from(updatedExpectedExpensesList).join(',')
        );
        onChange(
          assignmentId,
          AssignmentField.personalTransportationFromAssigneeShouldBookTheirOwnTicket,
          fieldTesterShouldBookTheirOwnTicketByDefault(newValue)
            ? 'true'
            : 'false'
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    personalTransportationFromAssignmentAssignmentTypeID,
    personalTransportationFromAssignmentCounty,
    mainAssignmentToCounty,
  ]);

  // adjust estimated start time if there is a last possible end time and the estimated duration or fixed assignment time changes
  useEffect(() => {
    // only change things if the assignment is not already approved
    if (currentAssignment.assignmentStatusID === AssignmentStatusEnum.Approved)
      return;
    // Numerisk guard (inte truthy-sträng): '0' är truthy, och utan meningsfull
    // uppdragstid skulle justeringen klobba starttiden till senast möjliga sluttid.
    if (
      state[AssignmentField.lastPossibleEndTime] &&
      (Number(state[AssignmentField.estimatedDuration]) > 0 ||
        Number(state[AssignmentField.fixedAssignmentTimeInHours]) > 0)
    ) {
      // Fast tid anges i timmar, estimerad tid i millisekunder — normalisera till ms
      // så att starttiden hamnar hela uppdragstiden före senast möjliga sluttid.
      const fixedTimeInHours = Number(
        state[AssignmentField.fixedAssignmentTimeInHours]
      );
      const newDurationMs =
        fixedTimeInHours > 0
          ? fixedTimeInHours * hourInMilliseconds
          : Number(state[AssignmentField.estimatedDuration]) || 0;
      const newEstStartTime = new Date(
        new Date(state[AssignmentField.lastPossibleEndTime]).getTime() -
          newDurationMs
      );
      if (!Number.isNaN(newEstStartTime.getTime())) {
        onChange(
          assignmentId,
          AssignmentField.estimatedStartTime,
          toInputDateTimeString(newEstStartTime)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assignmentId,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    state[AssignmentField.estimatedDuration],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    state[AssignmentField.fixedAssignmentTimeInHours],
  ]);

  // Uthyrd personal & Samåkning: estimerad starttid defaultar till 07:00 på det
  // bokade datumet vid skapande. Fylls bara i när fältet är tomt så att en manuellt
  // satt tid inte skrivs över.
  useEffect(() => {
    if (!isStaffRentalCaseType(currentCaseType)) return;
    if (currentAssignment.assignmentStatusID === AssignmentStatusEnum.Approved)
      return;
    if (assignmentId !== -1) return; // endast nya uppdrag
    if (state[AssignmentField.estimatedStartTime]) return; // redan satt
    const bookedTo = state[AssignmentField.bookedTo];
    if (!bookedTo) return;
    const defaultStart = new Date(bookedTo);
    if (Number.isNaN(defaultStart.getTime())) return;
    defaultStart.setHours(7, 0, 0, 0);
    onChange(
      assignmentId,
      AssignmentField.estimatedStartTime,
      toInputDateTimeString(defaultStart)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assignmentId,
    currentCaseType,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    state[AssignmentField.bookedTo],
  ]);

  useEffect(() => {
    // only change things if the assignment is not already approved
    if (currentAssignment.assignmentStatusID === AssignmentStatusEnum.Approved)
      return;
    // highlight if any of the other purchases has estimated duration and estimated starttime set in the form
    onChange(
      assignmentId,
      AssignmentField.hasMultiPurchaseAssignmentSiblingsWithEstimatedDurationAndStartTime,
      thereAreMultiplePurchasesForAssignedUser(
        Number(state[AssignmentField.assignedTo])
      )
        ? 'true'
        : 'false'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiplePurchasesList]);

  useEffect(
    () => {
      // only change things if the assignment is not already approved
      if (
        currentAssignment.assignmentStatusID === AssignmentStatusEnum.Approved
      )
        return;
      const fetchAverageDuration = async () => {
        // if (state)
        const { assignmentTypeID, fromCounty, toCounty, fromZip, toZip } =
          assignmentFormToAssignment(
            unmodifiedAssignment,
            state,
            usersById,
            zipCodeAreas
          );
        if (
          !fromCounty?.areaName ||
          !toCounty?.areaName ||
          !fromZip ||
          !toZip
        ) {
          return; // we need them for the average duration
        }

        // set estimated duration to hard-coded value if it's not already set

        if (
          state[AssignmentField.estimatedDuration] === '0' ||
          state[AssignmentField.estimatedDuration] === ''
        ) {
          const key =
            `${fromCounty.areaName}-${toCounty.areaName}`.toLowerCase();
          const hardcodedValue =
            hardCodedEstimatedDurations[currentCaseType]?.[key];
          if (hardcodedValue) {
            onChange(
              assignmentId,
              AssignmentField.estimatedDuration,
              hardcodedValue.toString()
            );
          }
        }
        // get the number of minutes from the average duration
        try {
          const result = await getAverageAssignmentDurationCall({
            caseType: currentCaseType,
            assignmentType: assignmentTypeID,
            fromZipCode: fromZip,
            toZipCode: toZip,
            fromCounty: fromCounty?.areaName,
            toCounty: toCounty?.areaName,
          });

          if (result !== null && result.sampleSize > 0) {
            // convert the number of seconds to milliseconds in order to use the estimatedDurationOptions array
            const millisAverage = result.average * 1000 * 60;
            // const millisMin = result.min * 1000 * 60;
            // const millisMax = result.max * 1000 * 60;

            // return the nearest value in the estimatedDurationOptions array
            const nearestAverage =
              esitimatedAssignmentDurations.getNearestEstimatedDuration(
                millisAverage
              );
            // const nearestMin =
            //   esitimatedAssignmentDurations.getNearestEstimatedDuration(
            //     millisMin
            //   );
            // const nearestMax =
            //   esitimatedAssignmentDurations.getNearestEstimatedDuration(
            //     millisMax
            //   );

            // note down our system estimates
            if (
              state[AssignmentField.systemEstimatedDurationExact] !==
              millisAverage.toString()
            ) {
              onChange(
                assignmentId,
                AssignmentField.systemEstimatedDurationExact,
                millisAverage.toString()
              );
            }
            if (
              state[AssignmentField.systemEstimatedDurationApprox] !==
              nearestAverage.toString()
            ) {
              onChange(
                assignmentId,
                AssignmentField.systemEstimatedDurationApprox,
                nearestAverage.toString()
              );
            }

            // setSuggestedEstimatedDuration({
            //   min: nearestMin,
            //   max: nearestMax,
            //   average: nearestAverage,
            // });
          } else {
            // setSuggestedEstimatedDuration(undefined);
          }
        } catch (error) {
          // do nothing
        }

        // setEstimatedDurationData(result ?? undefined);
      };
      if (avgDurationCallTimeoutRef.current) {
        clearTimeout(avgDurationCallTimeoutRef.current);
      }
      avgDurationCallTimeoutRef.current = setTimeout(fetchAverageDuration, 500);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentCaseType,
      zipCodeAreas,
      usersById,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      state[AssignmentField.assignedTo],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      state[AssignmentField.fromZip],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      state[AssignmentField.toZip],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      state[AssignmentField.assignmentTypeID],
    ]
  );

  // set estimated duration to fixedAssignmentTime if it is set
  useEffect(() => {
    if (
      state[AssignmentField.fixedAssignmentTimeInHours] !== '' &&
      state[AssignmentField.fixedAssignmentTimeInHours] !== '0'
    ) {
      const fixedAssignmentTimeInMilliseconds = Math.floor(
        Number(state[AssignmentField.fixedAssignmentTimeInHours]) *
          hourInMilliseconds
      );

      onChange(
        assignmentId,
        AssignmentField.estimatedDuration,
        fixedAssignmentTimeInMilliseconds.toString()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, state[AssignmentField.fixedAssignmentTimeInHours]]);

  // when the assignedto or the assignment id changes, check if the assignment is outside home are
  useEffect(() => {
    // only change things if the assignment is not already approved
    if (currentAssignment.assignmentStatusID === AssignmentStatusEnum.Approved)
      return;
    if (!currentAssignment.assignedTo) {
      onChange(
        currentAssignment.assignmentID,
        AssignmentField.fromAddressIsOutSideHomeArea,
        'false'
      );
      return;
    }
    // find user's postalCode
    const userPostalCode =
      usersById[currentAssignment.assignedTo?.userID]?.postalCode;

    if (!userPostalCode || !state[AssignmentField.fromZip]) {
      onChange(
        currentAssignment.assignmentID,
        AssignmentField.fromAddressIsOutSideHomeArea,
        'false'
      );
      return;
    }

    const result = zipCodeAreaHook.isOutsideAllowedRange(
      state[AssignmentField.fromZip],
      userPostalCode
    );

    onChange(
      currentAssignment.assignmentID,
      AssignmentField.fromAddressIsOutSideHomeArea,
      result ? 'true' : 'false'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAssignment.assignedTo, currentAssignment.assignmentID, usersById]);
};

export default useCaseAssignmentFormsEffects;
